import { useState, useRef, useCallback, useEffect, RefObject } from 'react'

interface UsePullToRefreshOptions {
  onRefresh: () => Promise<unknown>
  threshold?: number
  maxPull?: number
}

interface UsePullToRefreshReturn {
  containerRef: RefObject<HTMLDivElement | null>
  pullDistance: number
  isRefreshing: boolean
  isPulling: boolean
}

export function usePullToRefresh({
  onRefresh,
  threshold = 80,
  maxPull = 120,
}: UsePullToRefreshOptions): UsePullToRefreshReturn {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [pullDistance, setPullDistance] = useState(0)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isPulling, setIsPulling] = useState(false)

  const startY = useRef(0)
  const currentY = useRef(0)

  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      const touch = e.touches[0]
      // Only activate when at top of scroll
      if (touch && containerRef.current && containerRef.current.scrollTop === 0 && !isRefreshing) {
        startY.current = touch.clientY
        setIsPulling(true)
      }
    },
    [isRefreshing]
  )

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!isPulling || isRefreshing) return

      const touch = e.touches[0]
      if (!touch) return

      currentY.current = touch.clientY
      const distance = currentY.current - startY.current

      if (distance > 0) {
        // Apply resistance effect
        const resistedDistance = Math.min(distance * 0.5, maxPull)
        setPullDistance(resistedDistance)

        // Prevent default scroll behavior when pulling
        if (containerRef.current && containerRef.current.scrollTop === 0) {
          e.preventDefault()
        }
      }
    },
    [isPulling, isRefreshing, maxPull]
  )

  const handleTouchEnd = useCallback(async () => {
    if (!isPulling) return

    setIsPulling(false)

    if (pullDistance >= threshold && !isRefreshing) {
      setIsRefreshing(true)
      setPullDistance(threshold) // Keep at threshold during refresh

      try {
        await onRefresh()
      } finally {
        setIsRefreshing(false)
        setPullDistance(0)
      }
    } else {
      setPullDistance(0)
    }
  }, [isPulling, pullDistance, threshold, isRefreshing, onRefresh])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    container.addEventListener('touchstart', handleTouchStart, { passive: true })
    container.addEventListener('touchmove', handleTouchMove, { passive: false })
    container.addEventListener('touchend', handleTouchEnd)

    return () => {
      container.removeEventListener('touchstart', handleTouchStart)
      container.removeEventListener('touchmove', handleTouchMove)
      container.removeEventListener('touchend', handleTouchEnd)
    }
  }, [handleTouchStart, handleTouchMove, handleTouchEnd])

  return {
    containerRef,
    pullDistance,
    isRefreshing,
    isPulling,
  }
}
